import type { ReactNode } from 'react';
import { IdControl } from '../components/IdControl';
import { JsonViewerButton, TextViewerButton } from '../components/JsonViewer';
import { CollapsibleCodeBlock } from './CollapsibleCodeBlock';
import { ModelOutputView, type RunErrorRecordLike } from './ModelOutputView';

function accessoryRow(...nodes: (ReactNode | null | false | undefined)[]): ReactNode {
    const visible = nodes.filter(Boolean) as ReactNode[];
    if (visible.length === 0) return undefined;
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            {visible.map((n, i) => <span key={i} style={{ display: 'inline-flex' }}>{n}</span>)}
        </span>
    );
}

/**
 * Drill-down card for the currently-selected model run. Renders the card
 * scaffolding — title row + collapsible system/user prompt — then delegates the
 * error/output portion to the shared `ModelOutputView` so it stays identical to
 * the standalone Model Run detail page. Operation-specific content slots in via
 * `summary` (scores, above) and `children` (action modules, below).
 *
 * See docs/FeatureDesigns/operation-detail-unified-layout.md.
 */
export interface RunDetailCardProps {
    /** Title row — usually prompt name + provider tag + primary/type badges. */
    heading: ReactNode;
    error?: string | null;
    /** Structured error records — rendered as code/stage/provider chips below the banner. */
    errorRecords?: RunErrorRecordLike[];
    /** Quality-check badges slot — rendered in the Model Output block. */
    qualityBadges?: ReactNode;
    /** Rendered above the prompt/response collapsibles — scores, match factors. */
    summary?: ReactNode;
    systemPrompt?: string | null;
    userPrompt?: string | null;
    rawResponse?: string | null;
    /** Full LLM output incl. reasoning items (source of truth on new rows). */
    rawOutputFull?: string | null;
    /** Reasoning summary text (Responses API reasoning models). */
    reasoningSummary?: string | null;
    /** ModelInput id — surfaces an IdControl next to the user-prompt section. */
    modelInputId?: string | null;
    /** ModelOutput id — surfaces an IdControl next to the raw-response section. */
    modelOutputId?: string | null;
    /** Schema-aware parsed body — wrapped in the parsed collapsible. */
    parsed?: ReactNode;
    /** Title for the parsed collapsible. */
    parsedTitle?: string;
    /** Rendered after the collapsibles — action modules (overrides, feedback). */
    children?: ReactNode;
}

export function RunDetailCard({
    heading,
    error,
    errorRecords,
    qualityBadges,
    summary,
    systemPrompt,
    userPrompt,
    rawResponse,
    rawOutputFull,
    reasoningSummary,
    modelInputId,
    modelOutputId,
    parsed,
    parsedTitle = 'Parsed Feedback',
    children,
}: RunDetailCardProps) {
    const userPromptLabel = systemPrompt ? 'User Prompt Sent to LLM' : 'Prompt Sent to LLM';
    return (
        <div className="card prompt-details-card">
            <div className="prompt-details-header">
                <h3>{heading}</h3>
            </div>
            {summary}
            {systemPrompt && (
                <CollapsibleCodeBlock
                    title="System Prompt Sent to LLM"
                    text={systemPrompt}
                    titleAccessory={accessoryRow(
                        <TextViewerButton text={systemPrompt} title="System Prompt Sent to LLM" />,
                        <JsonViewerButton text={systemPrompt} title="System Prompt Sent to LLM" />,
                    )}
                />
            )}
            <CollapsibleCodeBlock
                title={userPromptLabel}
                text={userPrompt}
                titleAccessory={accessoryRow(
                    <TextViewerButton text={userPrompt} title={userPromptLabel} />,
                    <JsonViewerButton text={userPrompt} title={userPromptLabel} />,
                    modelInputId ? <IdControl value={modelInputId} container="modelInputs" /> : null,
                )}
            />
            <ModelOutputView
                error={error}
                errorRecords={errorRecords}
                qualityBadges={qualityBadges}
                reasoningSummary={reasoningSummary}
                rawOutputFull={rawOutputFull}
                rawResponse={rawResponse}
                modelOutputId={modelOutputId}
                parsed={parsed}
                parsedTitle={parsedTitle}
            />
            {children}
        </div>
    );
}
